/** Merge fields available when composing campaigns and templates. */
export const CAMPAIGN_MERGE_FIELD_KEYS = [
  'title',
  'firstName',
  'middleName',
  'lastName',
  'fullName',
  'email',
  'phone',
  'membershipNumber',
  'departmentName',
  'groupNames',
  'unitNames',
  'churchName',
] as const;

export const GIVING_MERGE_FIELD_KEYS = [
  'amount',
  'fundName',
  'receiptNumber',
  'contributionDate',
  'method',
] as const;

export type CampaignMergeFieldKey = (typeof CAMPAIGN_MERGE_FIELD_KEYS)[number];
export type GivingMergeFieldKey = (typeof GIVING_MERGE_FIELD_KEYS)[number];
export type MergeFieldKey = CampaignMergeFieldKey | GivingMergeFieldKey;

export const mergeFieldToken = (key: MergeFieldKey): string => `{{${key}}}`;

export interface MergeFieldValues {
  firstName: string;
  lastName: string;
  churchName: string;
  title?: string;
  middleName?: string;
  email?: string;
  phone?: string;
  membershipNumber?: string;
  departmentName?: string;
  groupNames?: string;
  unitNames?: string;
  firstNameFallback?: string;
}

const DEFAULT_FIRST_NAME_FALLBACK = 'friend';
const DEFAULT_CHURCH_NAME_FALLBACK = 'our church';

const optionalField = (value: string | null | undefined): string => value?.trim() ?? '';

const composeFullName = (values: MergeFieldValues, firstName: string): string => {
  const fullName = [values.firstName, values.middleName, values.lastName]
    .map((part) => part?.trim() ?? '')
    .filter(Boolean)
    .join(' ');
  return fullName || firstName;
};

/** Mirrors server merge-fields.ts for live message previews in compose. */
export const renderMergeFields = (template: string, values: MergeFieldValues): string => {
  const firstNameFallback = values.firstNameFallback?.trim() || DEFAULT_FIRST_NAME_FALLBACK;
  const firstName = values.firstName.trim() || firstNameFallback;
  const lastName = values.lastName.trim();
  const fullName = composeFullName(values, firstName);
  const churchName = values.churchName.trim() || DEFAULT_CHURCH_NAME_FALLBACK;

  return template
    .replaceAll(mergeFieldToken('title'), optionalField(values.title))
    .replaceAll(mergeFieldToken('firstName'), firstName)
    .replaceAll(mergeFieldToken('middleName'), optionalField(values.middleName))
    .replaceAll(mergeFieldToken('lastName'), lastName || firstName)
    .replaceAll(mergeFieldToken('fullName'), fullName)
    .replaceAll(mergeFieldToken('email'), optionalField(values.email))
    .replaceAll(mergeFieldToken('phone'), optionalField(values.phone))
    .replaceAll(mergeFieldToken('membershipNumber'), optionalField(values.membershipNumber))
    .replaceAll(mergeFieldToken('departmentName'), optionalField(values.departmentName))
    .replaceAll(mergeFieldToken('groupNames'), optionalField(values.groupNames))
    .replaceAll(mergeFieldToken('unitNames'), optionalField(values.unitNames))
    .replaceAll(mergeFieldToken('churchName'), churchName);
};

export const hasMergeFieldPlaceholders = (text: string): boolean => /\{\{\w+\}\}/.test(text);

export const insertAtSelection = (
  value: string,
  selectionStart: number,
  selectionEnd: number,
  insertion: string,
): { value: string; caret: number } => {
  const start = Math.max(0, Math.min(selectionStart, value.length));
  const end = Math.max(start, Math.min(selectionEnd, value.length));
  const next = `${value.slice(0, start)}${insertion}${value.slice(end)}`;
  return { value: next, caret: start + insertion.length };
};
